import React from "react";
import SearchPanel from "../SearchPanel/SearchPanel";
import { useSegmentList } from "./SegmentList.hooks";
import {
  filtersTags,
  itemsPerPageOptions,
  sortOptions,
} from "./SegmentList.helpers";
import SegmentListHeader from "./SegmentListHeader/SegmentListHeader";
import SegmentListContent from "./SegmentListContent/SegmentListContent";
import { SegmentListProvider } from "./SegmentList.context";
import DeleteListDialog from "../DeleteListDialog/DeleteListDialog";
import TagsDialog from "../TagsDialog";
import SegmentMoreDialog from "./SegmentMoreDialog/SegmentMoreDialog";
import SortDialog from "../SortDialog/SortDialog";
import SegmentItem from "./SegmentItem/SegmentItem";
import CloneSegmentDialog from "../CloneSegmentDialog/CloneSegmentDialog";
import { SegmentListProps } from "./SegmentList.types";
import { Box, CircularProgress } from "@mui/material";
import AddSegmentDialog from "../AddSegmentDialog";
import AudiencesListDialog from "../AudiencesListDialog";

function SegmentList(props: SegmentListProps) {
  const segmenListView = useSegmentList(props);

  const {
    changePage,
    changeSearch,
    changeSize,
    changeSort,
    cloneOpen,
    cloneSegment,
    closeClone,
    closeDeleteDialog,
    closeFilters,
    closeSort,
    data,
    deleteDialogOpen,
    deleteList,
    deleteSegments,
    deselectAll,
    enableSelectionActions,
    filtersOpen,
    isDeleteSegmentLoading,
    isLoading,
    mdUp,
    moreActions,
    pageable,
    removeTag,
    saveTags,
    search,
    selected,
    selectedDisabled,
    selectionDisabled,
    selectedTags,
    sortOpen,
    successClone,
    toggleSelect,
    toggleSelectAll,
    totalCount,
    visible,
    openExportAudienceDialog,
    handleCloseExportAudienceDialog,
    selectedItem,
    openExportToExistingDialog,
    handleCloseExportToExistingDialog,
    selectedItemForExport,
  } = segmenListView;

  if (!visible) {
    return null;
  }
  if (isLoading) {
    return (
      <Box
        width="100%"
        height="400px"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        bgcolor="white"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <SegmentListProvider value={segmenListView}>
      <SearchPanel
        ContentComponent={SegmentListContent}
        HeaderComponent={SegmentListHeader}
        MoreDialogComponent={SegmentMoreDialog}
        bgcolor="common.white"
        borderRadius={mdUp ? "8px" : "0px"}
        data={data}
        emptyStateMessage="Try some other search keywords. Tip: Try remembering the segment name"
        emptyStateTitle="No segments found"
        hideSearchOnSelect={enableSelectionActions}
        idKey="id"
        isLoading={isLoading}
        itemsPerPage={pageable.size}
        itemsPerPageOptions={itemsPerPageOptions}
        moreActions={moreActions}
        onDeselectAll={deselectAll}
        onItemsPerPageChange={changeSize}
        onPageChange={changePage}
        onSearch={changeSearch}
        onSort={changeSort}
        onTagDelete={removeTag}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
        page={pageable.page}
        search={search}
        selectable
        selected={selected}
        selectedDisabled={selectedDisabled}
        selectionDisabled={selectionDisabled}
        sort={pageable.sort}
        sortOptions={sortOptions}
        sortWidth={194}
        tags={selectedTags}
        totalCount={totalCount}
        disablePagination={props.disablePagination}
      />
      <DeleteListDialog
        data={deleteList}
        title="Delete segments?"
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onDelete={deleteSegments}
        ItemComponent={SegmentItem}
        idKey="id"
        deleting={isDeleteSegmentLoading}
      />
      <TagsDialog
        open={filtersOpen}
        onClose={closeFilters}
        onSave={saveTags}
        tags={props.filterTags ?? filtersTags}
        selected={selectedTags}
        subtitle="Type"
      />
      <SortDialog
        sortOptions={sortOptions}
        onClose={closeSort}
        open={sortOpen}
        sort={pageable.sort}
        onSave={changeSort}
      />
      <CloneSegmentDialog
        open={cloneOpen}
        segmentId={cloneSegment?.id}
        onClose={closeClone}
        onSuccess={successClone}
        name={cloneSegment?.name}
      />
      <AddSegmentDialog
        open={openExportAudienceDialog}
        title="Export as New Source"
        subtitle="This will create a source from selected dynamic segment."
        onClose={handleCloseExportAudienceDialog}
        mode="export"
        segmentIds={selectedItem?.id ? [selectedItem.id] : []}
      />
      <AudiencesListDialog
        open={openExportToExistingDialog}
        onClose={handleCloseExportToExistingDialog}
        mode="export"
        title="Export to Existing Source"
        subtitle="Please select the destination to export the data"
        segmentIds={selectedItemForExport?.id ? [selectedItemForExport.id] : []}
      />
    </SegmentListProvider>
  );
}

export default SegmentList;
